/**
 * EE Facets - AJAX Filtering for Collection Pages
 * Based on Dawn theme's facets.js
 * Handles filtering without page reload
 */

class EEFacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onFacetsRemovalClick = this.onFacetsRemovalClick.bind(this);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : window.location.search;
      EEFacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true, isLoadMore = false) {
    EEFacetFiltersForm.searchParams = searchParams;

    // Fetch the full page with search parameters
    const url = searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname;
    
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Update product grid
        const productGrid = html.getElementById('product-grid');
        const destinationProductGrid = document.getElementById('product-grid');
        if (productGrid && destinationProductGrid) {
          if (isLoadMore) {
            // Append products for load more
            destinationProductGrid.insertAdjacentHTML('beforeend', productGrid.innerHTML);
          } else {
            // Replace entire grid for filter changes
            destinationProductGrid.innerHTML = productGrid.innerHTML;
          }
        }

        // Update product count — hidden when filtered results are empty
        const countWrapper = document.querySelector('.ee-collection__count');
        const countContainer = document.querySelector('.ee-collection__count span');
        const newCountContainer = html.querySelector('.ee-collection__count span');
        const newCountWrapper = html.querySelector('.ee-collection__count');
        if (countWrapper && countContainer && newCountContainer) {
          countContainer.innerHTML = newCountContainer.innerHTML;
          countWrapper.hidden = false;
        } else if (countWrapper) {
          countWrapper.hidden = true;
        } else if (newCountWrapper) {
          // Count was absent (initially-empty collection) — insert it
          const sortWrapper = document.querySelector('.ee-collection__sort-wrapper');
          if (sortWrapper) sortWrapper.insertAdjacentHTML('beforeend', newCountWrapper.outerHTML);
        }

        // Update active facets
        const activeFacetsContainer = document.querySelector('.ee-facets__active');
        const newActiveFacetsContainer = html.querySelector('.ee-facets__active');
        if (activeFacetsContainer && newActiveFacetsContainer) {
          activeFacetsContainer.innerHTML = newActiveFacetsContainer.innerHTML;
          
          // Re-attach event listeners to new active filter links
          const newActiveFilterLinks = activeFacetsContainer.querySelectorAll('.ee-facets__active-tag');
          newActiveFilterLinks.forEach((link) => {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const searchParams = new URLSearchParams(link.href).toString();
              EEFacetFiltersForm.renderPage(searchParams);
            });
          });

          const newClearAllLink = activeFacetsContainer.querySelector('.ee-facets__clear-all');
          if (newClearAllLink) {
            newClearAllLink.addEventListener('click', (e) => {
              e.preventDefault();
              const searchParams = newClearAllLink.href;
              EEFacetFiltersForm.renderPage(searchParams);
            });
          }
        }

        // Update pagination
        const paginationContainer = document.querySelector('.ee-pagination');
        const newPaginationContainer = html.querySelector('.ee-pagination');
        if (paginationContainer && newPaginationContainer) {
          if (isLoadMore) {
            // For load more, only update if there's still a next page
            const hasLoadMoreButton = newPaginationContainer.querySelector('.ee-pagination__link--load-more');
            if (hasLoadMoreButton) {
              paginationContainer.innerHTML = newPaginationContainer.innerHTML;
              const facetFiltersForm = document.querySelector('ee-facet-filters-form');
              if (facetFiltersForm) {
                facetFiltersForm.attachPaginationListeners();
              }
            } else {
              // No more pages, hide pagination completely
              paginationContainer.remove();
            }
          } else {
            // Replace pagination for filter changes
            paginationContainer.innerHTML = newPaginationContainer.innerHTML;
            paginationContainer.style.display = 'flex';
            
            // Re-attach pagination event listeners
            const facetFiltersForm = document.querySelector('ee-facet-filters-form');
            if (facetFiltersForm) {
              facetFiltersForm.attachPaginationListeners();
            }
          }
        } else if (isLoadMore && paginationContainer && !newPaginationContainer) {
          // If new page has no pagination container at all, remove the existing one
          paginationContainer.remove();
        }

        // Update banner "N total items" counter
        const bannerCount = document.querySelector('.ee-collection-banner__items-count');
        const newBannerCount = html.querySelector('.ee-collection-banner__items-count');
        if (bannerCount && newBannerCount) {
          bannerCount.innerHTML = newBannerCount.innerHTML;
        }

        // Sync filter option counts/state so sidebar counts reflect applied filters
        EEFacetFiltersForm.syncFilterState(html);

        // Re-initialize custom web components in the updated content
        customElements.upgrade(document);
      })
      .catch((error) => {
        console.error('Error loading filtered results:', error);
      });

    if (updateURLHash) EEFacetFiltersForm.updateURLHash(searchParams);
  }

  static syncFilterState(html) {
    // Update checkbox + price inputs and their label counts without replacing
    // markup — preserves open <details> state, scroll, focus and listeners.
    html.querySelectorAll('.ee-facets__filters input').forEach((newInput) => {
      let selector;
      if (newInput.type === 'checkbox') {
        selector = `.ee-facets__filters input[name="${CSS.escape(newInput.name)}"][value="${CSS.escape(newInput.value)}"]`;
      } else {
        selector = `.ee-facets__filters input[name="${CSS.escape(newInput.name)}"]`;
      }
      document.querySelectorAll(selector).forEach((live) => {
        if (live.type === 'checkbox') {
          live.checked = newInput.checked;
          live.disabled = newInput.disabled;
        } else {
          live.value = newInput.value;
        }
        const newLabel = newInput.closest('.ee-facets__label');
        const liveLabel = live.closest('.ee-facets__label');
        if (newLabel && liveLabel) liveLabel.className = newLabel.className;
        const newCount = newLabel && newLabel.querySelector('.ee-facets__label-count');
        const liveCount = liveLabel && liveLabel.querySelector('.ee-facets__label-count');
        if (newCount && liveCount) liveCount.textContent = newCount.textContent;
      });
    });

    // Sync per-filter summary counts, matched by data-index within each form
    const newDetails = html.querySelectorAll('.ee-facets__filters .ee-facets__details');
    document.querySelectorAll('.ee-facets__filters').forEach((filtersEl) => {
      filtersEl.querySelectorAll('.ee-facets__details').forEach((liveDetails) => {
        const idx = liveDetails.dataset.index;
        const newDetails_el = idx
          ? Array.from(newDetails).find((d) => d.dataset.index === idx)
          : null;
        if (!newDetails_el) return;
        const newCountEl = newDetails_el.querySelector('.ee-facets__summary-count');
        const liveCountEl = liveDetails.querySelector('.ee-facets__summary-count');
        if (liveCountEl && newCountEl) {
          liveCountEl.textContent = newCountEl.textContent;
        } else if (liveCountEl && !newCountEl) {
          liveCountEl.remove();
        } else if (!liveCountEl && newCountEl) {
          const label = liveDetails.querySelector('.ee-facets__summary-label');
          if (label) label.appendChild(newCountEl.cloneNode(true));
        }
      });
    });
  }

  static renderActiveFacets(html) {
    const activeFacets = document.querySelector('.ee-facets__active');
    if (!activeFacets) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newActiveFacets = doc.querySelector('.ee-facets__active');

    if (newActiveFacets) {
      activeFacets.innerHTML = newActiveFacets.innerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url = searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname;
    history.pushState({ searchParams }, '', url);
  }

  static getSections() {
    // Get the section ID from the closest section element
    const sectionElement = document.querySelector('.section--collection-grid');
    if (!sectionElement) {
      console.error('Section element not found');
      return [];
    }
    
    // Extract section ID from the element's class or data attribute
    const sectionId = sectionElement.id.replace('ShopifySection-', '');
    if (!sectionId) {
      console.error('Section ID not found');
      return [];
    }
    
    return [
      {
        section: sectionId,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    
    // Remove empty values
    for (const [key, value] of searchParams.entries()) {
      if (!value) {
        searchParams.delete(key);
      }
    }
    
    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    EEFacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    const searchParams = this.createSearchParams(form);
    this.onSubmitForm(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const searchParams = new URLSearchParams(event.currentTarget.href).toString();
    EEFacetFiltersForm.renderPage(searchParams);
  }

  onFacetsRemovalClick(event) {
    event.preventDefault();
    const searchParams = event.currentTarget.href;
    EEFacetFiltersForm.renderPage(searchParams);
  }

  connectedCallback() {
    this.querySelector('form').addEventListener('submit', this.onSubmitHandler.bind(this));
    
    // Add event listeners for checkbox changes
    const checkboxes = this.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.onSubmitHandler.bind(this));
    });
    
    // Add event listeners for price input changes
    const priceInputs = this.querySelectorAll('.ee-facets__price-input');
    priceInputs.forEach((input) => {
      input.addEventListener('change', this.onSubmitHandler.bind(this));
    });
    
    const activeFilterLinks = this.querySelectorAll('.ee-facets__active-tag');
    activeFilterLinks.forEach((link) => {
      link.addEventListener('click', this.onActiveFilterClick);
    });

    const clearAllLink = this.querySelector('.ee-facets__clear-all');
    if (clearAllLink) {
      clearAllLink.addEventListener('click', this.onFacetsRemovalClick);
    }

    // Add event listeners for pagination links
    this.attachPaginationListeners();
  }

  attachPaginationListeners() {
    const paginationLinks = document.querySelectorAll('.ee-pagination__link--ajax');
    paginationLinks.forEach((link) => {
      link.removeEventListener('click', this.onPaginationClick);
      link.addEventListener('click', this.onPaginationClick.bind(this));
    });
  }

  onPaginationClick(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    const searchParams = url.search.substring(1); // Remove the leading '?'
    
    // Check if this is a load more button
    const isLoadMore = event.currentTarget.classList.contains('ee-pagination__link--load-more');
    
    EEFacetFiltersForm.renderPage(searchParams, null, true, isLoadMore);
  }
}

customElements.define('ee-facet-filters-form', EEFacetFiltersForm);

// Initialize listeners when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    EEFacetFiltersForm.setListeners();
  });
} else {
  EEFacetFiltersForm.setListeners();
}

class EEFacetRemove extends HTMLElement {
  constructor() {
    super();
    const element = this.querySelector('[data-clear-all]');
    if (element) {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        // For clear all, use empty search params to remove all filters
        EEFacetFiltersForm.renderPage('');
      });

      // Add keyboard support for Enter key
      element.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          element.click();
        }
      });
    }
  }
}

customElements.define('ee-facet-remove', EEFacetRemove);
